import util from 'preact-util';
import PubSub, { topics } from './pubsub';

const REFRESH_TOKEN_KEY = 'refreshToken';
let isRefreshing = false;
let refreshQueue = [];
let proactiveRefreshTimer = null;
let visibilityRefreshPromise = null;
let sessionWarningActive = false;

const MAX_TIMEOUT_DELAY = 2147483647; // 2^31-1, max safe setTimeout value
const RETRY_DELAYS = [2000, 5000, 10000]; // Backoff: 2s, 5s, 10s
const VISIBILITY_RETRY_DELAYS = [0, 2000, 5000, 10000, 20000]; // Backoff for wake-from-sleep: 0s, 2s, 5s, 10s, 20s (~37s total)

function decodeJwtExp(token) {
    try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        return payload.exp ? payload.exp * 1000 : null; // ms
    } catch (e) {
        return null;
    }
}

function isTokenFresh(token) {
    try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        if (!payload.exp || !payload.iat) return false;
        const totalLifetime = (payload.exp - payload.iat) * 1000;
        const remaining = payload.exp * 1000 - Date.now();
        return remaining > totalLifetime * 0.5;
    } catch (e) {
        return false;
    }
}

/**
 * Classify a refresh failure as 'transient' or 'auth_rejected'.
 * - auth_rejected: 401/403 WITH a JSON body — positive proof our backend rejected
 *   the refresh token. Only this may end the session.
 * - transient: everything else — network errors, 429, 5xx, proxy/CDN error pages,
 *   captive portals (which answer any URL with 200 + HTML, hence the JSON-body
 *   requirement), and other 4xx that don't mean revocation.
 */
export function classifyRefreshFailure(error, rawResponse, bodyIsJson) {
    // Network error (fetch threw) — always transient
    if (!rawResponse) {
        return 'transient';
    }
    const status = rawResponse.status;
    if ((status === 401 || status === 403) && bodyIsJson) {
        return 'auth_rejected';
    }
    return 'transient';
}

/**
 * Tokens may only be wiped on the reactive path (an API call already failed with
 * the current JWT). A proactive/background refresh keeps tokens on ANY failure —
 * if the session really is revoked, the reactive path will confirm and log out.
 */
export function shouldLogoutOnRefreshFailure(failureType, isProactive) {
    return failureType === 'auth_rejected' && !isProactive;
}

function scheduleProactiveRefresh(accessToken, doRefresh) {
    if (proactiveRefreshTimer) {
        clearTimeout(proactiveRefreshTimer);
        proactiveRefreshTimer = null;
    }
    const expiresAt = decodeJwtExp(accessToken);
    if (!expiresAt) return;
    const now = Date.now();
    const lifetime = expiresAt - now;
    if (lifetime <= 0) return;
    // Refresh at 80% of remaining lifetime (e.g. ~12 min for a 15-min token), min 10s
    // Cap to MAX_TIMEOUT_DELAY to prevent 32-bit integer overflow (fires immediately if > 2^31-1)
    const delay = Math.min(Math.max(lifetime * 0.8, 10000), MAX_TIMEOUT_DELAY);
    proactiveRefreshTimer = setTimeout(() => {
        doRefresh(true);
    }, delay);
}

/**
 * The backend mirrors the refresh token in an HttpOnly cookie (invisible to JS)
 * and sets a readable `hasSession=1` marker alongside it. When localStorage has
 * been wiped (Safari ITP after 7 days, Android storage eviction) the marker
 * tells us a silent cookie-based refresh is worth attempting.
 */
export function hasSessionMarker() {
    return typeof document !== 'undefined' && /(^|;\s*)hasSession=1(;|$)/.test(document.cookie);
}

/**
 * After a 401 (or a 403 with no usable JWT), decide what to do with the token
 * that is in storage NOW, given the token the failed request was sent with.
 *
 *  - "retry"   : the token changed while the request was in flight (a concurrent
 *                refresh renewed it), so the same request may succeed now.
 *  - "none"    : the same, still-valid token was just rejected. That is a real
 *                permission denial (musherUtil.notAllowed & co answer 401), and a
 *                retry would only re-send the identical rejected request. Before
 *                this check every denied call was sent twice, ~100 ms apart.
 *  - "refresh" : token missing or expired: go through the refresh path.
 *
 * Pure so it can be unit-tested; the interceptor closure calls it.
 */
export function resolveRetryAfterAuthFailure({ tokenAtSend, currentToken, now = Date.now() }) {
    const expiry = currentToken ? decodeJwtExp(currentToken) : null;
    if (currentToken && expiry && expiry > now) {
        return currentToken !== tokenAtSend ? 'retry' : 'none';
    }
    return 'refresh';
}

function dismissSessionWarning() {
    if (sessionWarningActive) {
        sessionWarningActive = false;
        PubSub.publish(topics.SESSION_RECONNECTED);
    }
}

export function installFetchApiInterceptor() {
    // Always remove PII from localStorage on startup (cleanup for existing users)
    localStorage.removeItem('userEmail');
    localStorage.removeItem('userCellphone');
    localStorage.removeItem('visitorCellphone');

    const originalFetchApi = util.fetchApi.bind(util);

    /**
     * Attempt a single token refresh call. Returns { success, failureType } where
     * failureType is 'transient' | 'auth_rejected' | null.
     */
    async function singleRefreshAttempt() {
        const refreshToken = localStorage.getItem(REFRESH_TOKEN_KEY);
        // No stored token and no cookie backup marker — nothing to try
        if (!refreshToken && !hasSessionMarker()) return { success: false, failureType: 'auth_rejected' };

        let rawResponse = null;
        let bodyIsJson = false;
        try {
            const apiServer = util.getApiServer();
            // credentials: 'include' sends the HttpOnly refresh cookie, which the
            // backend falls back to when the body token is missing or stale
            rawResponse = await fetch(`${apiServer}/api/sessions/refresh`, {
                method: 'POST',
                credentials: 'include',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(refreshToken ? { refreshToken } : {}),
            });
            let refreshResponse = null;
            try {
                refreshResponse = await rawResponse.json();
                bodyIsJson = true;
            } catch (parseErr) {
                // Non-JSON body (captive portal, proxy error page) — not our backend
                bodyIsJson = false;
            }

            if (rawResponse.status === 200 && refreshResponse && refreshResponse.data) {
                util.setJwtToken(refreshResponse.data.accessToken);
                localStorage.setItem(REFRESH_TOKEN_KEY, refreshResponse.data.refreshToken);
                return { success: true, failureType: null };
            }
            return { success: false, failureType: classifyRefreshFailure(null, rawResponse, bodyIsJson) };
        } catch (err) {
            return { success: false, failureType: classifyRefreshFailure(err, rawResponse, bodyIsJson) };
        }
    }

    async function doTokenRefresh(isProactive = false) {
        const refreshToken = localStorage.getItem(REFRESH_TOKEN_KEY);
        if ((!refreshToken && !hasSessionMarker()) || isRefreshing) return false;

        // Defense in depth: if the current token still has >50% of its lifetime
        // remaining, it was just refreshed by another flow — skip to break loops.
        const currentToken = util.getJwtToken();
        if (currentToken && isTokenFresh(currentToken)) {
            return true;
        }

        isRefreshing = true;
        try {
            // First attempt
            let result = await singleRefreshAttempt();

            if (result.success) {
                dismissSessionWarning();
                refreshQueue.forEach(cb => cb(true));
                refreshQueue = [];
                return true;
            }

            // Proven revocation on the reactive path — log out.
            // (Proactive failures of ANY kind keep tokens; see shouldLogoutOnRefreshFailure.)
            if (shouldLogoutOnRefreshFailure(result.failureType, isProactive)) {
                util.setJwtToken('');
                localStorage.removeItem(REFRESH_TOKEN_KEY);
                PubSub.publish(topics.LOG_OUT);
                refreshQueue.forEach(cb => cb(false));
                refreshQueue = [];
                return false;
            }

            // Any proactive failure — keep tokens intact for reactive retry
            if (isProactive) {
                refreshQueue.forEach(cb => cb(false));
                refreshQueue = [];
                return false;
            }

            // Reactive path: retry with backoff
            for (let i = 0; i < RETRY_DELAYS.length; i++) {
                await new Promise(resolve => setTimeout(resolve, RETRY_DELAYS[i]));

                // Re-check if another flow already refreshed the token
                const freshToken = util.getJwtToken();
                if (freshToken && isTokenFresh(freshToken)) {
                    dismissSessionWarning();
                    refreshQueue.forEach(cb => cb(true));
                    refreshQueue = [];
                    return true;
                }

                result = await singleRefreshAttempt();

                if (result.success) {
                    dismissSessionWarning();
                    refreshQueue.forEach(cb => cb(true));
                    refreshQueue = [];
                    return true;
                }

                // If the server now proves revocation, stop retrying and log out
                if (shouldLogoutOnRefreshFailure(result.failureType, isProactive)) {
                    util.setJwtToken('');
                    localStorage.removeItem(REFRESH_TOKEN_KEY);
                    PubSub.publish(topics.LOG_OUT);
                    refreshQueue.forEach(cb => cb(false));
                    refreshQueue = [];
                    return false;
                }
            }

            // All retries exhausted — show warning banner but do NOT log out.
            // Keep tokens so recovery is possible when connectivity returns.
            sessionWarningActive = true;
            PubSub.publish(topics.SESSION_WARNING);
            refreshQueue.forEach(cb => cb(false));
            refreshQueue = [];
            return false;
        } finally {
            isRefreshing = false;
        }
    }

    // Wrap setJwtToken to auto-schedule proactive refresh whenever a new token is set
    const originalSetJwtToken = util.setJwtToken.bind(util);
    util.setJwtToken = function(token) {
        originalSetJwtToken(token);
        if (token && token !== 'null' && token !== 'undefined' && token !== '') {
            scheduleProactiveRefresh(token, doTokenRefresh);
        } else if (proactiveRefreshTimer) {
            clearTimeout(proactiveRefreshTimer);
            proactiveRefreshTimer = null;
        }
    };

    async function attemptRefreshAndRetry(endpoint, opts, body, tokenAtSend) {
        // A still-valid token only justifies a retry if it is a DIFFERENT token
        // than the one that was just rejected (a concurrent refresh renewed it).
        // The same valid token being rejected is a real permission denial.
        const decision = resolveRetryAfterAuthFailure({ tokenAtSend, currentToken: util.getJwtToken() });
        if (decision === 'retry') {
            return await originalFetchApi(endpoint, opts, body);
        }
        if (decision === 'none') {
            return null;
        }

        const refreshToken = localStorage.getItem(REFRESH_TOKEN_KEY);
        if (!refreshToken && !hasSessionMarker()) return null;

        if (!isRefreshing) {
            const success = await doTokenRefresh();
            if (success) {
                return await originalFetchApi(endpoint, opts, body);
            }
        } else {
            // Another refresh is in progress, wait for it
            const success = await new Promise(resolve => {
                refreshQueue.push(resolve);
            });
            if (success) {
                return await originalFetchApi(endpoint, opts, body);
            }
        }
        return null;
    }

    util.fetchApi = async function interceptedFetchApi(endpoint, opts = {}, body = {}) {
        // Wait for any in-flight visibility-triggered refresh before sending requests
        if (visibilityRefreshPromise) {
            await visibilityRefreshPromise;
        }

        // Remember which token this request goes out with. preact-util deletes
        // opts.jwtToken while sending, so it has to be read before the call.
        const tokenAtSend = opts.jwtToken || util.getJwtToken();
        let response = await originalFetchApi(endpoint, opts, body);

        // Once the LOG_OUT handler fires, ignore everything the interceptor
        // would otherwise do with tokens. The page is about to reload and any
        // migrationTokens / refresh attempt at this point would resurrect the
        // session we're explicitly tearing down.
        const isLoggingOut = typeof window !== 'undefined' && window.__loggingOut;

        // Handle migration tokens from /api/users/info
        if (!isLoggingOut && response.data && response.data.migrationTokens) {
            util.setJwtToken(response.data.migrationTokens.accessToken);
            localStorage.setItem(REFRESH_TOKEN_KEY, response.data.migrationTokens.refreshToken);
            // Remove PII from localStorage now that we use userId-based tokens
            localStorage.removeItem('userEmail');
            localStorage.removeItem('userCellphone');
            localStorage.removeItem('visitorCellphone');
            delete response.data.migrationTokens;
        }

        // On 401, attempt refresh (skip for the refresh endpoint itself)
        if (!isLoggingOut && response.status === 401 && endpoint !== '/api/sessions/refresh') {
            const retried = await attemptRefreshAndRetry(endpoint, opts, body, tokenAtSend);
            if (retried) {
                response = retried;
            }
        }

        // On 403, check if it's due to expired/missing JWT (not a real permission denial)
        if (!isLoggingOut && response.status === 403 && endpoint !== '/api/sessions/refresh') {
            const token = util.getJwtToken();
            const expiry = token ? decodeJwtExp(token) : null;
            if (!token || !expiry || expiry <= Date.now()) {
                // JWT is missing or expired — this 403 is likely from restrictAPI seeing no req.user
                const retried = await attemptRefreshAndRetry(endpoint, opts, body, tokenAtSend);
                if (retried) {
                    response = retried;
                }
            }
            // If JWT is valid, it's a real permission denied — leave 403 as-is
        }

        return response;
    };

    // Schedule proactive refresh for existing token on startup
    const currentToken = util.getJwtToken();
    if (currentToken && currentToken !== 'null' && currentToken !== 'undefined') {
        scheduleProactiveRefresh(currentToken, doTokenRefresh);
    } else if (localStorage.getItem(REFRESH_TOKEN_KEY) || hasSessionMarker()) {
        // No usable JWT but a session credential remains — either a stored
        // refresh token, or only the cookie backup after localStorage was wiped
        // (Safari ITP, Android storage eviction). Attempt one silent recovery
        // and let boot API calls wait for it so the app renders logged in.
        visibilityRefreshPromise = doTokenRefresh(true).finally(() => {
            visibilityRefreshPromise = null;
        });
    }

    // Visibility-triggered refresh with multiple retries and network awareness.
    // WiFi after laptop wake can take 5-10+ seconds to reconnect, so we retry
    // at 0s, 2s, 5s, 10s, 20s (~37s total window).
    async function visibilityRefreshWithRetries() {
        for (let i = 0; i < VISIBILITY_RETRY_DELAYS.length; i++) {
            if (VISIBILITY_RETRY_DELAYS[i] > 0) {
                await new Promise(resolve => setTimeout(resolve, VISIBILITY_RETRY_DELAYS[i]));
            }

            // Skip attempt if browser reports offline — wait for next retry
            if (!navigator.onLine) {
                continue;
            }

            // Re-check: another flow may have refreshed the token during the wait
            const freshToken = util.getJwtToken();
            if (freshToken && isTokenFresh(freshToken)) {
                return true;
            }

            const success = await doTokenRefresh(true);
            if (success) {
                return true;
            }
        }
        return false;
    }

    // Refresh session when tab is restored (e.g. iOS kills/backgrounds tab, laptop sleep)
    document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'visible') {
            const refreshToken = localStorage.getItem(REFRESH_TOKEN_KEY);
            if (!refreshToken && !hasSessionMarker()) return; // Not logged in

            const token = util.getJwtToken();
            const expiry = token ? decodeJwtExp(token) : null;

            if (!token || !expiry || expiry <= Date.now()) {
                // JWT is missing or expired — retry with network-aware backoff
                visibilityRefreshPromise = visibilityRefreshWithRetries().finally(() => {
                    visibilityRefreshPromise = null;
                });
            } else {
                // JWT still valid — reschedule proactive refresh (timer may have been killed)
                scheduleProactiveRefresh(token, doTokenRefresh);
            }
        }
    });

    // When OS restores connectivity (e.g. WiFi reconnects after sleep), immediately
    // attempt a token refresh if the JWT is expired. This handles the case where
    // visibilitychange fires before WiFi is back.
    window.addEventListener('online', () => {
        const refreshToken = localStorage.getItem(REFRESH_TOKEN_KEY);
        if (!refreshToken && !hasSessionMarker()) return; // Not logged in

        const token = util.getJwtToken();
        const expiry = token ? decodeJwtExp(token) : null;

        if (!token || !expiry || expiry <= Date.now()) {
            doTokenRefresh(true);
        }
    });
}

/**
 * Returns the current visibility-triggered refresh promise, or null if none is in progress.
 * Used by app.js to wait for token refresh before making API calls on wake.
 */
export function getVisibilityRefreshPromise() {
    return visibilityRefreshPromise;
}
